/**
 * POS checkout payment types.
 *
 * Which buttons the checkout shows, in what order, and the store's own payment types
 * ("Venmo", "Check", "House account"), from `store_data.checkout_payment_types`:
 *
 *   { order: ['CARD', 'CASH', 'c_7f3a9k', ...],
 *     custom: [{ id: 'c_7f3a9k', name: 'Venmo', enabled: true, open_drawer: false }] }
 *
 * The built-in buttons keep their on/off switches (the store's feature flags); `order` only
 * sorts them. A custom type pays exactly like Other, cash-priced, with its name on the order.
 * The native POS (business_app `CheckoutPaymentTypes.kt`) applies the same rules.
 */

export const MAX_CUSTOM_PAYMENT_TYPES = 12;
export const CUSTOM_PAYMENT_NAME_MAX = 24;

/** The built-in checkout buttons in their default order, with the flag that switches each on. */
export const BUILTIN_PAYMENT_TYPES: { id: string; label: string; flag: string; hint: string }[] = [
  { id: 'CARD', label: 'Card', flag: 'card_enabled', hint: 'Charge the card terminal' },
  { id: 'SPLIT', label: 'Split', flag: 'split_payment_enabled', hint: 'Split the check between guests' },
  { id: 'CASH', label: 'Cash', flag: 'cash_enabled', hint: 'Tender cash and open the drawer' },
  { id: 'GIFT', label: 'Gift', flag: 'gift_enabled', hint: 'Paid in full by a gift, no money taken' },
  { id: 'MANUAL_CARD', label: 'Manual Card', flag: 'manual_card_enabled', hint: 'Type the card details in' },
  { id: 'KEYED', label: 'Keyed', flag: 'keyed_payment_enabled', hint: 'Card keyed on an outside terminal' },
  { id: 'GIFT_CARDS', label: 'Gift Cards', flag: 'gift_cards_enabled', hint: 'Redeem a Labrador gift card' },
  { id: 'THIRD_PARTY', label: 'Third Party', flag: 'third_party_enabled', hint: 'Paid through a delivery app' },
  { id: 'OTHER', label: 'Other', flag: 'other_payment_enabled', hint: 'Any other tender, cash-priced' },
];

/** Names a custom type cannot take: tenders the backend already gives a meaning to. */
export const RESERVED_PAYMENT_NAMES = new Set([
  'CASH', 'CREDIT', 'DEBIT', 'CARD', 'CREDIT_CARD', 'DEBIT_CARD', 'STORE_CREDIT', 'GIFT',
  'GIFT_CARD', 'GIFT_CARDS', 'GIFTCARD', 'SPLIT', 'PAY_LATER', 'CREDIT_KEYED', 'KEYED',
  'MANUAL', 'MANUAL_CARD', 'THIRD_PARTY', 'OTHER', 'COMP', 'TIP', 'REFUND',
]);

export interface CustomPaymentType {
  id: string;
  name: string;
  enabled: boolean;
  /** Kick the cash drawer after the payment, as Cash does. */
  open_drawer: boolean;
}

export interface CheckoutPaymentTypes {
  order: string[];
  custom: CustomPaymentType[];
}

/** Upper case with spaces and hyphens as underscores: how tenders are compared everywhere. */
export function normalizePaymentName(name: any): string {
  return `${name ?? ''}`.trim().replace(/[\s-]+/g, '_').toUpperCase();
}

export function isCustomPaymentId(id: any): boolean {
  return typeof id === 'string' && /^c_[a-z0-9]{4,16}$/.test(id);
}

/** The store's setting, cleaned: every custom type has an id, a name and both switches. */
export function checkoutPaymentTypes(store: any): CheckoutPaymentTypes {
  const raw = store?.store_data?.checkout_payment_types || {};
  const custom: CustomPaymentType[] = (Array.isArray(raw.custom) ? raw.custom : [])
    .filter((c: any) => c && typeof c === 'object' && `${c.name || ''}`.trim())
    .map((c: any) => ({
      id: `${c.id || ''}`,
      name: `${c.name}`.trim(),
      enabled: c.enabled !== false,
      open_drawer: c.open_drawer === true,
    }));
  const order: string[] = (Array.isArray(raw.order) ? raw.order : []).filter((k: any) => typeof k === 'string');
  return { order, custom };
}

/** The store's custom payment types that the checkout offers. */
export function enabledCustomPaymentTypes(store: any): CustomPaymentType[] {
  return checkoutPaymentTypes(store).custom.filter(c => c.enabled && c.id);
}

export function customPaymentTypeById(store: any, id: any): CustomPaymentType | null {
  return checkoutPaymentTypes(store).custom.find(c => c.id === id) || null;
}

/** The custom type an order or split leg was paid with, matched by name (any case or spacing). */
export function customPaymentTypeByName(store: any, name: any): CustomPaymentType | null {
  const key = normalizePaymentName(name);
  if (!key) { return null; }
  return checkoutPaymentTypes(store).custom.find(c => normalizePaymentName(c.name) === key) || null;
}

export function isCustomPaymentName(store: any, name: any): boolean {
  return !!customPaymentTypeByName(store, name);
}

/**
 * Sort checkout entries by the store's order: listed ids first, in that order, then the rest
 * in the order given (the built-in default). `idOf` names each entry's id.
 */
export function arrangePaymentTypes<T>(store: any, entries: T[], idOf: (e: T) => string): T[] {
  const order = checkoutPaymentTypes(store).order;
  const rank = new Map<string, number>();
  order.forEach((id, i) => { if (!rank.has(id)) { rank.set(id, i); } });
  return entries
    .map((entry, i) => ({ entry, i, r: rank.has(idOf(entry)) ? rank.get(idOf(entry)) : order.length + i }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map(x => x.entry);
}

/** Why a custom name cannot be used, or '' when it can. `except` is the type being renamed. */
export function customPaymentNameError(store: any, name: string, except?: string): string {
  const clean = `${name || ''}`.trim().replace(/\s+/g, ' ');
  if (!clean) { return 'Give the payment type a name.'; }
  if (clean.length > CUSTOM_PAYMENT_NAME_MAX) { return `At most ${CUSTOM_PAYMENT_NAME_MAX} characters.`; }
  const key = normalizePaymentName(clean);
  if (RESERVED_PAYMENT_NAMES.has(key)) { return `"${clean}" is already a payment type.`; }
  const other = checkoutPaymentTypes(store).custom.find(c => c.id !== except && normalizePaymentName(c.name) === key);
  if (other) { return `There is already a payment type called "${other.name}".`; }
  return '';
}
